import "dayjs/locale/uk";

import type { FormInstance } from "antd";
import { Checkbox, DatePicker, Flex, Form } from "antd";
import ukUA from "antd/es/date-picker/locale/uk_UA";
import dayjs, { Dayjs } from "dayjs";
import weekOfYear from "dayjs/plugin/weekOfYear";
import { FC, useMemo } from "react";
import { backendDateFormat } from "tm-shared/constants";

import { WEEKDAY_OPTIONS } from "@/constants/ui/weekdays";
import { PrimaryText } from "@/shared/ui/Typography";
import { handleFocus } from "@/utils/dom/handleFocus";

import { ScheduleRepeatFormFields } from "./ScheduleRepeatForm.types";
import { getWeekDeclension } from "./ScheduleRepeatForm.utils";
import {
    StyledCalendarWrapper,
    StyledCell,
    StyledContainer,
    StyledInputWeekly,
    StyledWeekDays,
} from "./Styled";

dayjs.extend(weekOfYear);
dayjs.locale("uk");

interface ScheduleRepeatFormProps {
    form: FormInstance<ScheduleRepeatFormFields>;
    disabled?: boolean;
}

export const ScheduleRepeatForm: FC<ScheduleRepeatFormProps> = ({
    form,
    disabled,
}) => {
    const today = dayjs();
    const startDate = today.startOf("day");

    const disabledDate = (current: Dayjs) => current.isBefore(startDate, "day");
    const customFormat = (v: Dayjs) => `${v.week()} тиждень ${v.year()} року`;

    // значення у формі: string | undefined (YYYY-MM-DD)
    const weekStartDate = Form.useWatch("weekStartDate", form);
    const weekly = Form.useWatch("weekly", form);

    const selectedDate = useMemo(() => {
        const d = weekStartDate ? dayjs(weekStartDate) : today.startOf("week");
        return d.isValid() ? d : today.startOf("week");
    }, [weekStartDate, today]);

    const cellRender = (current: string | number | Dayjs) => {
        const currentDay = dayjs(current);
        const startOfWeek = selectedDate.startOf("week");
        const endOfWeek = selectedDate.endOf("week");

        const isInWeek =
            currentDay.isAfter(startOfWeek.subtract(1, "day")) &&
            (currentDay.isBefore(endOfWeek) ||
                currentDay.isSame(endOfWeek, "day"));
        const isStart = currentDay.isSame(startOfWeek, "day");
        const isEnd = currentDay.isSame(endOfWeek, "day");

        return (
            <StyledCell $isInWeek={isInWeek} $isStart={isStart} $isEnd={isEnd}>
                {currentDay.date()}
            </StyledCell>
        );
    };

    return (
        <StyledContainer>
            <PrimaryText fontSize={16}>Повторення заходу</PrimaryText>

            <Flex vertical gap={12}>
                <PrimaryText>Повторювати з інтервалом:</PrimaryText>

                <Flex gap={12} justify="flex-start" align="center">
                    <PrimaryText>Раз на</PrimaryText>

                    <Form.Item<ScheduleRepeatFormFields>
                        name="weekly"
                        noStyle
                        rules={[
                            { required: true, message: "Вкажіть інтервал." },
                        ]}
                        getValueFromEvent={(value) =>
                            value ? Number(value) : 1
                        }
                    >
                        <StyledInputWeekly
                            placeholder="кількість"
                            min={1}
                            inputMode="numeric"
                            onFocus={handleFocus}
                            parser={(value) => (value ?? "").replace(/\D/g, "")}
                            disabled={disabled}
                        />
                    </Form.Item>

                    <PrimaryText>{getWeekDeclension(weekly)}</PrimaryText>
                </Flex>
            </Flex>

            <Flex vertical gap={12}>
                <PrimaryText>Дні повторення:</PrimaryText>

                <StyledWeekDays>
                    <Form.Item<ScheduleRepeatFormFields>
                        name="days"
                        rules={[
                            {
                                validator: async (_, value?: number[]) => {
                                    if (value && value.length > 0) return;
                                    throw new Error(
                                        "Оберіть хоча б один день повторення.",
                                    );
                                },
                            },
                        ]}
                    >
                        <Checkbox.Group disabled={disabled}>
                            {WEEKDAY_OPTIONS.map(({ label, value }) => (
                                <Checkbox key={value} value={value}>
                                    {label}
                                </Checkbox>
                            ))}
                        </Checkbox.Group>
                    </Form.Item>
                </StyledWeekDays>
            </Flex>

            <Flex vertical gap={12}>
                <PrimaryText>Почати з:</PrimaryText>

                <StyledCalendarWrapper>
                    <Form.Item<ScheduleRepeatFormFields>
                        name="weekStartDate"
                        noStyle
                        valuePropName="value"
                        getValueProps={(value) => ({
                            value: value
                                ? dayjs(value, backendDateFormat.date, true)
                                : undefined,
                        })}
                        getValueFromEvent={(date: Dayjs | null) =>
                            date
                                ? date
                                      .startOf("week")
                                      .format(backendDateFormat.date)
                                : undefined
                        }
                    >
                        <DatePicker
                            locale={ukUA}
                            disabledDate={disabledDate}
                            cellRender={cellRender}
                            format={customFormat}
                            allowClear={false}
                            showNow={false}
                            disabled={disabled}
                            size="large"
                            classNames={{ popup: { root: "week-calendar" } }}
                        />
                    </Form.Item>
                </StyledCalendarWrapper>
            </Flex>
        </StyledContainer>
    );
};
